<template>
  <div class="p-8" v-shortkey="shortcuts" @shortkey="triggerShortkey">
    <!-- Tabs -->
    <ul class="nav nav-tabs p-1" role="tablist" :data-selected="currentTab">
      <li class="nav-item">
        <button @click="handleCurrentTab" data-tab="requested" class="nav-link btn btn-link">
          Requested
        </button>
      </li>
      <li class="nav-item" v-if="showSent">
        <button @click="handleCurrentTab" data-tab="sent" class="nav-link btn btn-link">
          Sent
        </button>
      </li>
      <li class="nav-item" v-if="showReceived">
        <button @click="handleCurrentTab" data-tab="received" class="nav-link btn btn-link">
          Received
        </button>
      </li>
      <li class="nav-item">
        <button @click="handleCurrentTab" data-tab="resulted" class="nav-link btn btn-link">
          Resulted
        </button>
      </li>
      <li class="nav-item">
        <button @click="handleCurrentTab" data-tab="all" class="nav-link btn btn-link">All</button>
      </li>
    </ul>
    <div
      v-for="tab in availableTabs"
      :key="tab.name"
      v-show="currentTab === tab.name"
      class="tab-content tab-pane fade show active"
    >
      <!-- Your component template goes here -->
      <DxGridWithSearch
        v-if="tab.name === currentTab"
        @initialized="initializeGrid"
        @content-ready="onContentReady"
        :selection="selectionMode"
        :dataSource="source"
        :columnChooser="true"
        :editing="editing"
        :toolbar="toolbar"
        keyExpr="id"
        :columns="tab.columns"
        ref="grid"
        :gridName="tab.gridName"
        noDataText="No tests found"
      >
        <template v-slot:actionsTemplate="{ data: { data, key, rowIndex } }">
          <div
            class="justify-content-start d-flex align-items-center"
            :data-testid="'actions-col-' + rowIndex"
          >
            <router-link
              v-if="permissions.CaseView"
              v-tooltip.right="'Result Case'"
              :to="'/accession/' + data.caseId + '/cytology-results'"
              class="btn-link btn p-0 mr-2"
            >
              <icon class="text-primary" icon="clinic-medical" />
            </router-link>
            <icon-button
              v-if="permissions.PathReportView"
              v-tooltip.right="'View path report.'"
              class="btn text-primary pointer p-0 px-1 mr-1"
              @click="handleViewPathReport(data.caseId)"
              icon="eye"
            />
          </div>
        </template>
      </DxGridWithSearch>
    </div>

    <modal :status="isViewerOpen" @close="isViewerOpen = !isViewerOpen">
      <PDFViewer
        class="pdf-modal no-scroll"
        :url="currentPdf"
        @close="isViewerOpen = !isViewerOpen"
        @print="handlePrint"
        :fileName="fileName"
        @download="handlePdfDownload"
      />
    </modal>
    <div class="d-flex justify-content-between mt-3 py-4 px-2">
      <div class="d-flex group">
        <button :disabled="reportLoading" @click="handlePrintReport" class="btn btn-primary">
          <span class="d-inline-flex align-items-center">
            <loader v-if="reportLoading" class="mr-2" size="small"></loader>
            <span> <u>P</u>rint </span>
          </span>
        </button>
      </div>

      <div class="d-flex group">
        <button @click="handleChange" class="btn btn-primary"><u>C</u>hange</button>
      </div>
    </div>

    <PathReportViewer v-if="isInline && isActivated" class="mt-2" @close="closeReport" />
  </div>
</template>

<script>
import DxGridWithSearch from "./common/DxGridWithSearch.vue";
import { mapGetters, mapState } from "vuex";
import cytologyService from "@/services/cytology";
import {
  createLogItem,
  filterAccessionNumber,
  formatDatetimeCell,
  getAltKeys
} from "@/modules/helpers";
import PathReportViewer from "./Reports/PathReportViewer.vue";
import IconButton from "./common/IconButton.vue";
import Icon from "./common/Icon.vue";
import { map, switchMap, distinctUntilChanged } from "rxjs/operators";
import { AuditLogItems, CytMolecTestStatusEnum, enumToDropDown } from "@/modules/enums";
import CustomStore from "devextreme/data/custom_store";
import auditLog from "@/services/AuditLog";
import { ReportsApi } from "@/services";
import DataSource from "devextreme/data/data_source";
import notify from "devextreme/ui/notify";
export default {
  name: "CytologyLabGrid",
  components: {
    DxGridWithSearch,
    PathReportViewer,
    IconButton,
    Icon,
    PDFViewer: () => import("./common/PDFViewer.vue"),
    modal: () => import("./common/Modal.vue"),
    loader: () => import("./common/Loader.vue")
  },
  props: {
    // Define your props here
  },
  data() {
    return {
      isViewerOpen: false,
      currentPdf: null,
      reportLoading: false,
      fileName: "",
      currentTab: "requested",
      editing: {
        allowUpdating: true,
        allowAdding: false,
        mode: "cell"
      },
      shortcuts: getAltKeys("c", "p"),
      reportCaseId: null,
      grid: null,
      isActivated: true,
      selectionMode: {
        allowSelectAll: true,
        mode: "multiple",
        showCheckBoxesMode: "always"
      }
      // Define your component data here
    };
  },
  activated() {
    this.isActivated = true;
    if (this.grid.refresh) {
      this.grid.refresh(true);
    }
    this.closeReport();
  },
  deactivated() {
    this.isActivated = false;
    if (this.currentPdf) {
      URL.revokeObjectURL(this.currentPdf);
      this.currentPdf = null;
    }
  },
  domStreams: ["contentReady$"],
  subscriptions() {
    const availableResults$ = this.contentReady$.pipe(
      map(({ component }) => {
        const rows = component.getVisibleRows();
        return rows.map(e => e.data.cytMolecTestId);
      }),
      distinctUntilChanged((prev, curr) => {
        return JSON.stringify(prev) === JSON.stringify(curr);
      }),
      switchMap(ids => {
        const setData = new Set(ids);
        return cytologyService.getAvailableResults(Array.from(setData));
      }),
      map(data => {
        return data.reduce((acc, curr) => {
          acc[curr.id] = curr.results;
          return acc;
        }, {});
      })
    );

    const registeredRows = this.contentReady$.pipe(
      map(({ component }) => {
        const rows = component.getVisibleRows();
        return rows.reduce((acc, curr) => {
          acc[curr.data.id] = curr.data;
          return acc;
        }, {});
      })
    );
    return {
      availableResults$,
      registeredRows
    };
  },

  methods: {
    async handleLoadReport() {
      try {
        this.reportLoading = true;
        const currentFilter = this.filters;
        const store = cytologyService.getLabCytoTests();
        const currentRows = await store.load({
          filter: currentFilter,
          select: ["id"]
        });
        const keys = currentRows.map(e => e.id);
        const data = {
          format: 0,
          outputFormat: "pdf",
          reportId: null,
          labId: this.labId,
          cytMolecTestSpecimensIds: keys
        };
        const file = await ReportsApi.getCytologyTests(data);
        const blob = new Blob([file], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        this.currentPdf = url;
        this.isViewerOpen = true;
        this.fileName = `CytologyTests.pdf`;
        const logItem = createLogItem({}, AuditLogItems.Print);
        logItem.comments = `Printed cyt tests report PDF.`;
        auditLog.insertLogMessage(logItem);
      } catch (error) {
        console.error("Error printing report:", error);
      } finally {
        this.reportLoading = false;
      }
    },
    async initializeGrid({ component }) {
      this.grid = component;
      this.grid.option("refreshMode", "full");
    },
    handleCurrentTab(event) {
      const { tab } = event.target.dataset;
      this.currentTab = tab;
      console.log("Tab set to:", tab);
    },
    async triggerShortkey(event) {
      const { srcKey } = event;
      if (srcKey === "c") {
        this.handleChange();
      } else if (srcKey === "p") {
        this.handleLoadReport();
      }
    },
    async handlePdfDownload() {
      const logItem = createLogItem({}, AuditLogItems.Download);
      logItem.comments = `Manually downloaded cytology tests report.`;
      await auditLog.insertLogMessage(logItem);
    },
    async handlePrint() {
      const logItem = createLogItem({}, AuditLogItems.Print);
      logItem.comments = `Printed cytology tests report.`;
      await auditLog.insertLogMessage(logItem);
    },
    async handlePrintReport() {
      try {
        const filterOptions = this.grid.getCombinedFilter();
        // Get Selected rows if there are any, otherwise use the current filters to print all rows in the current view
        let selectedRowKeys = this.grid.getSelectedRowKeys();
        if (selectedRowKeys.length < 1) {
          const allRowKeys = await this.store.load({ filter: filterOptions, select: ["id"] });
          selectedRowKeys = allRowKeys.map(e => e.id);
        }
        const printerId = this.DefaultCytologyTestPrinter || null;
        await ReportsApi.printCytologyTests({
          molecularTestResultIds: selectedRowKeys,
          printerId
        });
        const logItem = createLogItem({}, AuditLogItems.Print);
        logItem.comments = `Printed cytology tests report to printer ${printerId}.`;
        await auditLog.insertLogMessage(logItem);
      } catch (error) {
        console.error("Error sending print job:", error);
        notify(
          "An error occurred while sending the print job. Please try again or contact your administrator.",
          "error",
          5000
        );
      } finally {
        this.reportLoading = false;
        window.notify(
          `A print job has been sent to the  ${
            this.DefaultCytologyTestPrinter ? "user set printer" : "lab default"
          }. If you are not seeing the print job, please check the printer or contact your administrator.`
        );
      }
    },
    onContentReady(event) {
      this.contentReady$?.next(event);
    },
    handleViewPathReport(caseId) {
      if (this.isActivated) {
        this.$store.dispatch("report/viewPathReport", { caseId });
      }
    },
    closeReport() {
      this.$store.commit("report/toggleReportViewer", false);
    },
    handleChange() {
      if (this.currentTab === "sent") {
        if (this.showReceived) {
          this.grid.editCell(0, "cytMolecTestStatusId");
        } else {
          this.grid.editCell(0, "cytMolecResultId");
        }
      } else if (this.currentTab === "requested") {
        if (this.showSent || this.showReceived) {
          this.grid.editCell(0, "cytMolecTestStatusId");
        } else {
          this.grid.editCell(0, "cytMolecResultId");
        }
      } else if (this.currentTab === "all") {
        this.grid.editCell(0, "cytMolecTestStatusId");
      } else {
        this.grid.editCell(0, "cytMolecResultId");
      }
    }
  },
  computed: {
    ...mapGetters(["permissions"]),
    ...mapGetters("report", ["isInline"]),
    ...mapState({
      showSent: state => state.labSettings.CytologyMolecularTestShowSentStatus,
      showReceived: state => state.labSettings.CytologyMolecularTestShowReceivedStatus,
      AccessionNumberingType: state => state.labSettings.AccessionNumberingType,
      DefaultCytologyTestPrinter: state => state.applicationSettings.defaultCytologyTestPrinter,
      labId: state => state.currentLab
    }),
    routeCaseId() {
      return this.$route.params.caseId;
    },
    cytoTestsGridName() {
      return `CytologyTestsGrid-${this.currentTab}`;
    },
    availableTabs() {
      const tabs = [];
      if (this.showSent) {
        tabs.push({ name: "sent", columns: this.sentColumns, gridName: "CytoTestTab_Sent" });
      }
      if (this.showReceived) {
        tabs.push({
          name: "received",
          columns: this.receivedColumns,
          gridName: "CytoTestTab_Received"
        });
      }
      return [
        { name: "requested", columns: this.requestedColumns, gridName: "CytoTestTab_Requested" },
        ...tabs,
        { name: "resulted", columns: this.resultedColumns, gridName: "CytoTestTab_Resulted" },
        { name: "all", columns: this.allColumns, gridName: "CytoTestTab_All" }
      ];
    },
    statusOptions() {
      let options = enumToDropDown(CytMolecTestStatusEnum);
      if (this.showSent) {
        if (this.currentTab === "requested") {
          options = options.filter(
            e => e.id == CytMolecTestStatusEnum.Sent || e.id == CytMolecTestStatusEnum.Requested
          );
        }
      } else {
        options = options.filter(e => e.id != CytMolecTestStatusEnum.Sent);
      }

      if (this.showReceived) {
        if (this.currentTab === "sent") {
          options = options.filter(
            e => e.id == CytMolecTestStatusEnum.Received || e.id == CytMolecTestStatusEnum.Sent
          );
        }
      } else {
        options = options.filter(e => e.id != CytMolecTestStatusEnum.Received);
      }
      return options;
    },
    filters() {
      const filters = [];
      if (this.currentTab === "requested") {
        filters.push(["cytMolecTestStatusId", CytMolecTestStatusEnum.Requested]);
      } else if (this.currentTab === "sent") {
        filters.push(["cytMolecTestStatusId", CytMolecTestStatusEnum.Sent]);
      } else if (this.currentTab === "received") {
        filters.push(["cytMolecTestStatusId", CytMolecTestStatusEnum.Received]);
      } else if (this.currentTab === "resulted") {
        filters.push(["cytMolecTestStatusId", CytMolecTestStatusEnum.Resulted]);
      }
      return filters;
    },
    source() {
      const filters = this.filters;

      const innerStore = cytologyService.getLabCytoTests();

      const store = new CustomStore({
        key: "id",
        load: options => {
          return innerStore.load(options);
        },
        update: (key, data) => {
          const dataRow = this.registeredRows[key];
          const payload = {
            id: key,
            specimenId: dataRow.specimenId,
            cytMolecTestId: dataRow.cytMolecTestId,
            ...data
          };
          if (this.currentTab != "resulted" && data.cytMolecResultId) {
            payload.cytMolecTestStatusId = CytMolecTestStatusEnum.Resulted;
          }
          return cytologyService.resultTestSpecimen(dataRow.specimenId, payload);
        }
      });
      return new DataSource({
        store,
        keyExpr: "id",
        filter: filters?.length > 0 ? filters : null,
        reshapeOnPush: true
      });
    },
    actionColumn() {
      return {
        type: "command",
        allowSearch: false,
        allowEditing: false,
        caption: "Actions",
        cellTemplate: "actionsTemplate"
      };
    },
    resultColumn() {
      return {
        dataField: "cytMolecResultId",
        caption: "Result",
        allowEditing: true,
        calculateDisplayValue(data) {
          return data.cytMolecResult;
        },
        calculateFilterExpression(value) {
          return ["cytMolecResult", "startsWith", value];
        },
        lookup: {
          dataSource: options => {
            if (options.data) {
              const { cytMolecTestId } = options.data;
              if (cytMolecTestId && this.availableResults$[cytMolecTestId]) {
                return this.availableResults$[cytMolecTestId];
              }
            }
            return [];
          },
          displayExpr: "resultDisplayText",
          valueExpr: "cytMolecResultId"
        }
      };
    },
    baseColumns() {
      let allowEditingStatus = true;
      if (this.currentTab === "requested" && !this.showSent && !this.showReceived) {
        allowEditingStatus = false;
      }
      return [
        { dataField: "numberPrefix", caption: "Prefix" },
        { dataField: "numberYear", caption: "Year" },
        { dataField: "numberSequence", caption: "Number" },
        {
          dataField: "caseNumber",
          allowEditing: false,
          calculateFilterExpression: filterAccessionNumber(
            "caseNumber",
            this.AccessionNumberingType
          )
        },
        { dataField: "requestedBy", allowEditing: false },
        { dataField: "patientName", caption: "Patient", allowEditing: false },
        { dataField: "cytMolecTest", caption: "Test", allowEditing: false },
        {
          dataField: "cytMolecTestStatusId",
          caption: "Status",
          allowEditing: allowEditingStatus,
          lookup: {
            dataSource: this.statusOptions,
            displayExpr: "displayName",
            valueExpr: "id"
          }
        },
        {
          dataField: "diagnosis",
          allowEditing: false,
          allowSearch: false,
          calculateCellValue(data) {
            if (data.hasMultipleCytDiagnoses) {
              return "Multiple Diagnoses";
            }
            return data.diagnosis;
          }
        }
      ];
    },
    requestedColumns() {
      let cols = [
        ...this.baseColumns,
        {
          dataField: "requestedOn",
          allowEditing: false,
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.requestedOn) {
              return formatDatetimeCell(data.requestedOn);
            }
            return "";
          }
        }
      ];
      if (!this.showSent && !this.showReceived) {
        cols = [...cols, this.resultColumn];
      }

      return [...cols, this.actionColumn];
    },
    sentColumns() {
      let col = [
        ...this.baseColumns,
        {
          dataField: "sentOn",
          dataType: "datetime",
          allowEditing: false,
          calculateCellValue(data) {
            if (data.sentOn) {
              return formatDatetimeCell(data.sentOn);
            }
            return "";
          }
        }
      ];

      if (!this.showReceived) {
        col = [...col, this.resultColumn];
      }

      return [...col, this.actionColumn];
    },
    receivedColumns() {
      let col = [
        ...this.baseColumns,
        {
          dataField: "sentOn",
          dataType: "datetime",
          allowEditing: false,
          calculateCellValue(data) {
            if (data.sentOn) {
              return formatDatetimeCell(data.sentOn);
            }
            return "";
          }
        },
        {
          dataField: "receivedOn",
          dataType: "datetime",
          allowEditing: false,
          calculateCellValue(data) {
            if (data.receivedOn) {
              return formatDatetimeCell(data.receivedOn);
            }
            return "";
          }
        },
        this.resultColumn
      ];

      return [...col, this.actionColumn];
    },
    resultedColumns() {
      return [
        ...this.baseColumns,
        this.resultColumn,
        {
          dataField: "resultedOn",
          dataType: "datetime",
          allowEditing: false,
          calculateCellValue(data) {
            if (data.resultedOn) {
              return formatDatetimeCell(data.resultedOn);
            }
            return "";
          }
        },
        this.actionColumn
      ];
    },
    allColumns() {
      return [
        ...this.baseColumns,
        {
          dataField: "queuedOn",
          allowEditing: false,
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.queuedOn) {
              return formatDatetimeCell(data.queuedOn);
            }
            return "";
          }
        },
        {
          allowEditing: false,
          dataField: "sentOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.sentOn) {
              return formatDatetimeCell(data.sentOn);
            }
            return "";
          }
        },
        {
          dataField: "resultedOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.resultedOn) {
              return formatDatetimeCell(data.resultedOn);
            }
            return "";
          }
        },
        this.resultColumn,
        this.actionColumn
      ];
    },
    columns() {
      if (this.currentTab === "requested") {
        return this.requestedColumns;
      } else if (this.currentTab === "sent") {
        return this.sentColumns;
      } else if (this.currentTab === "received") {
        return this.receivedColumns;
      }
      return [
        ...this.baseColumns,
        {
          dataField: "queuedOn",
          allowEditing: false,
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.queuedOn) {
              return formatDatetimeCell(data.queuedOn);
            }
            return "";
          }
        },
        {
          allowEditing: false,
          dataField: "sentOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.sentOn) {
              return formatDatetimeCell(data.sentOn);
            }
            return "";
          }
        },
        {
          dataField: "resultedOn",
          dataType: "datetime",
          calculateCellValue(data) {
            if (data.resultedOn) {
              return formatDatetimeCell(data.resultedOn);
            }
            return "";
          }
        },
        this.resultColumn,
        this.actionColumn
      ];
    },
    toolbar() {
      return {
        items: [{ name: "saveButton", visible: true }]
      };
    }
    // Define your computed properties here
  },
  watch: {
    // Define your watchers here
  }
};
</script>

<style scoped>
.group {
  gap: 5px;
}
.pdf-modal {
  width: 50vw;
  height: 80vh;
}
.no-scroll {
  overflow-y: hidden;
}
.hidden {
  display: none;
}
[data-selected="sent"] button[data-tab="sent"],
[data-selected="requested"] button[data-tab="requested"],
[data-selected="resulted"] button[data-tab="resulted"],
[data-selected="received"] button[data-tab="received"],
[data-selected="all"] button[data-tab="all"] {
  background-color: #007bff;
  color: white;
}
</style>
